import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants } from 'utils';
import { MoneyService } from 'services';
import { Loading } from 'components';
import { ArrowLeft } from 'assets/icons';
import {
	Chart,
	BarController,
	BarElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	Tooltip as ChartTooltip,
	Legend,
	DoughnutController
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(
	BarController,
	BarElement,
	LinearScale,
	CategoryScale,
	ArcElement,
	ChartTooltip,
	Legend,
	DoughnutController,
	ChartDataLabels
);

const TYPE = {
	PHYSICAL: 'physical',
	DIGITAL: 'digital',
	LIQUIDITY: 'liquidity'
} as const;

type TypeKey = typeof TYPE[keyof typeof TYPE];

const TYPE_LABELS: Record<TypeKey, string> = {
	[TYPE.PHYSICAL]: 'Activos físicos',
	[TYPE.DIGITAL]: 'Activos digitales',
	[TYPE.LIQUIDITY]: 'Liquidez'
};

const TYPE_COLORS: Record<TypeKey, string> = {
	[TYPE.PHYSICAL]: '#22cd68',
	[TYPE.DIGITAL]: '#1a89e5',
	[TYPE.LIQUIDITY]: '#f1fc25'
};

const DIGITAL_SUBTYPES = [
	{ key: 'STOCKS', label: 'Acciones', type: Constants.MONEY_CATEGORIES.STOCKS },
	{ key: 'CRYPTO', label: 'Criptos', type: Constants.MONEY_CATEGORIES.CRYPTO },
	{ key: 'BONDS', label: 'Bonos', type: Constants.MONEY_CATEGORIES.BONDS }
];

// Paleta Holdz / Ámbar / Acero
const PALETTE = {
	crypto:    '#22C55E',
	stocks:    '#F59E0B',
	bonds:     '#64748B',
	digital:   '#22C55E',
	liquidity: '#94A3B8',
	physical:  '#22C55E'
};

// Paleta categórica para segmentos dentro de cada dona (cuando hay >1 posición)
const CATEGORICAL = [
	'#3B82F6',
	'#EF4444',
	'#FACC15',
	'#22C55E',
	'#F97316',
	'#14B8A6',
	'#93C5FD',
	'#FCA5A5',
	'#FDE68A',
	'#86EFAC'
];

const getCategoricalColors = (count: number): string[] => {
	const out: string[] = [];
	for (let i = 0; i < count; i++) {
		out.push(CATEGORICAL[i % CATEGORICAL.length]);
	}
	return out;
};

const hexToRgba = (hex: string, alpha: number): string => {
	const h = hex.replace('#', '');
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	return `rgba(${ r }, ${ g }, ${ b }, ${ alpha })`;
};

interface Position {
	name: string;
	total_amount: number;
	subtype?: string;
}

interface ClientInfo {
	name?: string;
	lastname?: string;
	email?: string;
}

interface Props extends RouteComponentProps<{ userId: string }> {
	dispatch: any;
}

interface State {
	loading: boolean;
	client: ClientInfo | null;
	totals: Record<TypeKey, number>;
	positions: Record<TypeKey, Position[]>;
	errors: TypeKey[];
}

class ClientDashboard extends React.Component<Props, State> {

	doughnutRef = React.createRef<HTMLCanvasElement>();
	cryptoDonutRef = React.createRef<HTMLCanvasElement>();
	stocksDonutRef = React.createRef<HTMLCanvasElement>();
	digitalDonutRef = React.createRef<HTMLCanvasElement>();
	liquidityDonutRef = React.createRef<HTMLCanvasElement>();

	doughnutChart: any = null;
	cardCharts: Record<string, any> = {};

	state: State = {
		loading: true,
		client: null,
		totals: {
			[TYPE.PHYSICAL]: 0,
			[TYPE.DIGITAL]: 0,
			[TYPE.LIQUIDITY]: 0
		},
		positions: {
			[TYPE.PHYSICAL]: [],
			[TYPE.DIGITAL]: [],
			[TYPE.LIQUIDITY]: []
		},
		errors: []
	};

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: 'Patrimonio del cliente',
				onBack: null
			}
		});

		const stateClient = (this.props.location.state as any)?.client;
		this.setState({ client: stateClient || null });

		this.load();
	}

	componentWillUnmount() {
		this.destroyCharts();
		const tooltipEl = document.getElementById('chart-tooltip');
		if (tooltipEl) tooltipEl.style.opacity = '0';
	}

	destroyCharts = () => {
		if (this.doughnutChart) {
			this.doughnutChart.destroy();
			this.doughnutChart = null;
		}
		Object.keys(this.cardCharts).forEach((k) => {
			this.cardCharts[k]?.destroy();
		});
		this.cardCharts = {};
	};

	load = async () => {
		const userId = this.props.match.params.userId;

		const calls = [
			{ key: TYPE.PHYSICAL, p: MoneyService.admin.physical.view({ user_id: userId, category_id: '', withoutLoading: true }) },
			{ key: TYPE.LIQUIDITY, p: MoneyService.admin.liquidity.view({ user_id: userId, category_id: '', withoutLoading: true }) },
			...DIGITAL_SUBTYPES.map((s) => ({
				key: `digital:${s.key}`,
				p: MoneyService.admin.digital.view({ user_id: userId, type: s.type, withoutLoading: true })
			}))
		];

		const results = await Promise.allSettled(calls.map((c) => c.p));

		const totals = { ...this.state.totals };
		const positions: Record<TypeKey, Position[]> = {
			[TYPE.PHYSICAL]: [],
			[TYPE.DIGITAL]: [],
			[TYPE.LIQUIDITY]: []
		};
		const errors: TypeKey[] = [];

		// Physical
		const physRes = results[0];
		if (physRes.status === 'fulfilled') {
			const r: any = physRes.value;
			totals[TYPE.PHYSICAL] = Number(r?.total) || 0;
			positions[TYPE.PHYSICAL] = this.normalizePositions(r?.data);
		} else {
			errors.push(TYPE.PHYSICAL);
		}

		// Liquidity
		const liqRes = results[1];
		if (liqRes.status === 'fulfilled') {
			const r: any = liqRes.value;
			totals[TYPE.LIQUIDITY] = Number(r?.total) || 0;
			positions[TYPE.LIQUIDITY] = this.normalizePositions(r?.data);
		} else {
			errors.push(TYPE.LIQUIDITY);
		}

		// Digital (3 subtipos)
		let digitalTotal = 0;
		let digitalPositions: Position[] = [];
		let digitalAnyOk = false;
		let digitalAnyFail = false;
		DIGITAL_SUBTYPES.forEach((s, idx) => {
			const res = results[2 + idx];
			if (res.status === 'fulfilled') {
				digitalAnyOk = true;
				const r: any = res.value;
				digitalTotal += Number(r?.total) || 0;
				const tagged = this.normalizePositions(r?.data).map((p) => ({ ...p, subtype: s.label }));
				digitalPositions = digitalPositions.concat(tagged);
			} else {
				digitalAnyFail = true;
			}
		});
		totals[TYPE.DIGITAL] = digitalTotal;
		positions[TYPE.DIGITAL] = digitalPositions;
		if (digitalAnyFail && !digitalAnyOk) {
			errors.push(TYPE.DIGITAL);
		}

		this.setState({
			loading: false,
			totals,
			positions,
			errors
		}, () => {
			this.renderCharts();
		});
	};

	normalizePositions = (data: any): Position[] => {
		if (!Array.isArray(data)) return [];
		return data
			.map((item: any) => ({
				name: item?.name || item?.symbol || 'Sin nombre',
				total_amount: Number(item?.total_amount) || 0
			}))
			.filter((p) => p.total_amount > 0);
	};

	renderCharts = () => {
		this.destroyCharts();
		this.renderDoughnut();
		this.renderCardDonut('crypto', this.cryptoDonutRef, this.getSubtypePositions('Criptos'));
		this.renderCardDonut('stocks', this.stocksDonutRef, this.getSubtypePositions('Acciones'));
		this.renderCardDonut('digital', this.digitalDonutRef, this.state.positions[TYPE.DIGITAL] || [], { cutout: '65%' });
		this.renderCardDonut('liquidity', this.liquidityDonutRef, this.state.positions[TYPE.LIQUIDITY] || [], { cutout: '65%' });
	};

	getSubtypePositions = (label: string): Position[] => {
		const digital = this.state.positions[TYPE.DIGITAL] || [];
		return digital.filter((p) => p.subtype === label);
	};

	renderCardDonut = (
		key: string,
		ref: React.RefObject<HTMLCanvasElement>,
		positions: Position[],
		opts: { cutout?: string } = {}
	) => {
		const canvas = ref.current;
		if (!canvas) return;
		if (positions.length === 0) return;
		const sorted = [...positions].sort((a, b) => b.total_amount - a.total_amount);
		const subtotal = sorted.reduce((acc, p) => acc + p.total_amount, 0);
		const colors = getCategoricalColors(sorted.length);
		try {
			this.cardCharts[key] = new Chart(canvas, {
				type: 'doughnut',
				data: {
					labels: sorted.map((p) => p.name),
					datasets: [{
						data: sorted.map((p) => p.total_amount),
						backgroundColor: colors,
						borderWidth: 0
					}]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					cutout: opts.cutout || '60%',
					layout: { padding: 0 },
					interaction: { mode: 'nearest', intersect: true },
					plugins: {
						legend: { display: false },
						// @ts-ignore
						datalabels: { display: false },
						tooltip: {
							enabled: false,
							external: (context: any) => {
								const tooltipEl = document.getElementById('chart-tooltip');
								if (!tooltipEl) return;

								const tooltipModel = context.tooltip;

								if (tooltipModel.opacity === 0) {
									tooltipEl.style.opacity = '0';
									return;
								}

								if (tooltipModel.body) {
									const dp = tooltipModel.dataPoints[0];
									const dataIndex = dp.dataIndex;
									const value = dp.parsed;
									const name = dp.label || '';
									const bg = dp.dataset.backgroundColor;
									const color = Array.isArray(bg) ? bg[dataIndex] : bg;

									const data = context.chart.data.datasets[0].data as number[];
									const cardSubtotal = data.reduce((a: number, b: number) => a + (Number(b) || 0), 0);

									let text: string;
									if (cardSubtotal <= 0) {
										text = `${ name }: ${ Globals.formatMiles(value) }`;
									} else {
										const p = (value / cardSubtotal) * 100;
										text = `${ name }: ${ Globals.formatMiles(value) } · ${ p.toFixed(1).replace('.', ',') }%`;
									}

									tooltipEl.innerHTML = `
										<span class="tooltip-color-dot" style="background:${ color }"></span>
										<span class="tooltip-text">${ text }</span>
									`;
								}

								const canvasRect = context.chart.canvas.getBoundingClientRect();
								const x = canvasRect.left + tooltipModel.caretX;
								const y = canvasRect.top + tooltipModel.caretY;

								tooltipEl.style.opacity = '1';
								tooltipEl.style.left = x + 'px';
								tooltipEl.style.top = (y - 12) + 'px';
							}
						}
					}
				}
			});
		} catch (e) { console.log(e); }
	};

	renderDoughnut = () => {
		const canvas = this.doughnutRef.current;
		if (!canvas) return;
		const { totals } = this.state;
		const total = totals[TYPE.PHYSICAL] + totals[TYPE.DIGITAL] + totals[TYPE.LIQUIDITY];
		if (total <= 0) return;
		try {
			this.doughnutChart = new Chart(canvas, {
				type: 'doughnut',
				data: {
					labels: [TYPE_LABELS[TYPE.PHYSICAL], TYPE_LABELS[TYPE.DIGITAL], TYPE_LABELS[TYPE.LIQUIDITY]],
					datasets: [{
						data: [totals[TYPE.PHYSICAL], totals[TYPE.DIGITAL], totals[TYPE.LIQUIDITY]],
						backgroundColor: ['#3B82F6', '#EF4444', '#FACC15'],
						borderWidth: 0
					}]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					cutout: '60%',
					layout: { padding: { top: 10, bottom: 10, left: 10, right: 10 } },
					plugins: {
						legend: { display: false },
						// @ts-ignore
						datalabels: {
							anchor: 'center',
							align: 'center',
							color: '#fff',
							font: { weight: 'bold', size: 12 },
							backgroundColor: 'rgba(0, 0, 0, 0.55)',
							borderRadius: 4,
							padding: { top: 3, bottom: 3, left: 6, right: 6 },
							display: (ctx: any) => {
								const v = ctx.dataset.data[ctx.dataIndex] || 0;
								return total > 0 && (v / total) * 100 >= 5;
							},
							formatter: (value: number) => {
								const p = total > 0 ? (value / total) * 100 : 0;
								return p.toFixed(1).replace('.', ',') + '%';
							}
						}
					}
				}
			});
		} catch (e) { console.log(e); }
	};

	goBack = () => {
		if (this.props.history.length > 1) {
			this.props.history.goBack();
		} else {
			this.props.history.push('/admin/money');
		}
	};

	percent = (part: number, total: number) => {
		if (!total || total <= 0) return '—';
		const pct = (part / total) * 100;
		return pct.toFixed(1).replace('.', ',') + '%';
	};

	percentNum = (part: number, total: number) => {
		if (!total || total <= 0) return 0;
		return (part / total) * 100;
	};

	renderError = () => {
		const { errors } = this.state;
		if (errors.length === 0) return null;
		const names = errors.map((e) => TYPE_LABELS[e]).join(', ');
		return (
			<div className="error-banner">
				No se pudo cargar: <strong>{ names }</strong>. Mostrando el resto del patrimonio.
			</div>
		);
	};

	grandTotal = () => {
		const { totals } = this.state;
		return totals[TYPE.PHYSICAL] + totals[TYPE.DIGITAL] + totals[TYPE.LIQUIDITY];
	};

	truncate = (s: string, n: number = 18) => (s && s.length > n ? s.substring(0, n - 1) + '…' : s);

	initialsFromClient = () => {
		const { client } = this.state;
		const pick = (s?: string) => (s && s.trim() ? s.trim().charAt(0).toUpperCase() : '');
		if (!client) return '?';
		return (pick(client.name) + pick(client.lastname)) || '?';
	};

	renderHeader = () => {
		const { client, totals, positions } = this.state;
		const userId = this.props.match.params.userId;
		const grand = this.grandTotal();

		const fullName = client
			? `${ client.name || '' } ${ client.lastname || '' }`.trim() || `Cliente #${ userId }`
			: `Cliente #${ userId }`;
		const email = client?.email || '—';

		const digital = positions[TYPE.DIGITAL] || [];
		const acciones = digital.filter((p) => p.subtype === 'Acciones').length;
		const criptos = digital.filter((p) => p.subtype === 'Criptos').length;
		const bonos = digital.filter((p) => p.subtype === 'Bonos').length;
		const totalDigital = digital.length;

		const pcts = {
			[TYPE.PHYSICAL]: grand > 0 ? totals[TYPE.PHYSICAL] / grand : 0,
			[TYPE.DIGITAL]: grand > 0 ? totals[TYPE.DIGITAL] / grand : 0,
			[TYPE.LIQUIDITY]: grand > 0 ? totals[TYPE.LIQUIDITY] / grand : 0
		};
		const sortedTypes = ([TYPE.PHYSICAL, TYPE.DIGITAL, TYPE.LIQUIDITY] as TypeKey[])
			.sort((a, b) => pcts[b] - pcts[a]);
		const topType = sortedTypes[0];
		const hasAnyExposure = grand > 0;

		const allPositions: Array<Position & { _type: TypeKey }> = [
			...(positions[TYPE.PHYSICAL] || []).map((p) => ({ ...p, _type: TYPE.PHYSICAL })),
			...(positions[TYPE.DIGITAL] || []).map((p) => ({ ...p, _type: TYPE.DIGITAL })),
			...(positions[TYPE.LIQUIDITY] || []).map((p) => ({ ...p, _type: TYPE.LIQUIDITY }))
		];
		const top3 = allPositions.sort((a, b) => b.total_amount - a.total_amount).slice(0, 3);

		const badgeFor = (item: Position & { _type: TypeKey }) => {
			if (item._type === TYPE.PHYSICAL) return { text: 'Físico', cls: 'physical' };
			if (item._type === TYPE.LIQUIDITY) return { text: 'Liquidez', cls: 'liquidity' };
			if (item.subtype === 'Acciones') return { text: 'Acciones', cls: 'stocks' };
			if (item.subtype === 'Criptos') return { text: 'Criptos', cls: 'crypto' };
			if (item.subtype === 'Bonos') return { text: 'Bonos', cls: 'bonds' };
			return { text: 'Digital', cls: 'crypto' };
		};

		const exposureColor = (() => {
			if (topType === TYPE.PHYSICAL) return PALETTE.physical;
			if (topType === TYPE.DIGITAL) return PALETTE.digital;
			return PALETTE.liquidity;
		})();

		return (
			<div className="dash-header">
				<div className="dash-header-toolbar">
					<button className="btn-back-holdz" onClick={ this.goBack }>
						<img src={ ArrowLeft } /> Volver
					</button>
				</div>
				<div className="dash-header-grid">
					<div className="dash-col dash-col-identity">
						<div className="identity-row">
							<div className="avatar-rect">{ this.initialsFromClient() }</div>
							<div className="identity-text">
								<div className="identity-name">{ fullName }</div>
								<div className="identity-id">ID #{ userId }</div>
							</div>
						</div>
						<div className="identity-divider"></div>
						<div className="identity-email-block">
							<span className="micro-label">Email</span>
							<div className="identity-email">{ email }</div>
						</div>
					</div>

					<div className="dash-col dash-col-total">
						<span className="total-label">Total Patrimonio</span>
						{ grand > 0 ? (
							<div className="total-amount">{ Globals.formatMiles(grand) }</div>
						) : (
							<div className="total-amount empty">Sin patrimonio registrado</div>
						) }
						<div className="total-body">
							<div className="total-doughnut-wrapper">
								<canvas ref={ this.doughnutRef }></canvas>
							</div>
							<ul className="total-distribution">
								<li>
									<span className="dot" style={{ backgroundColor: '#3B82F6' }}></span>
									<span className="dist-label">{ TYPE_LABELS[TYPE.PHYSICAL] }</span>
									<span className="dist-pct">{ this.percent(totals[TYPE.PHYSICAL], grand) }</span>
									<span className="dist-amount">{ Globals.formatMiles(totals[TYPE.PHYSICAL]) }</span>
								</li>
								<li>
									<span className="dot" style={{ backgroundColor: '#EF4444' }}></span>
									<span className="dist-label">{ TYPE_LABELS[TYPE.DIGITAL] }</span>
									<span className="dist-pct">{ this.percent(totals[TYPE.DIGITAL], grand) }</span>
									<span className="dist-amount">{ Globals.formatMiles(totals[TYPE.DIGITAL]) }</span>
								</li>
								<li>
									<span className="dot" style={{ backgroundColor: '#FACC15' }}></span>
									<span className="dist-label">{ TYPE_LABELS[TYPE.LIQUIDITY] }</span>
									<span className="dist-pct">{ this.percent(totals[TYPE.LIQUIDITY], grand) }</span>
									<span className="dist-amount">{ Globals.formatMiles(totals[TYPE.LIQUIDITY]) }</span>
								</li>
							</ul>
						</div>
					</div>

					<div className="dash-col dash-col-kpis">
						<div className="kpi-block">
							<span className="micro-label">Posiciones digitales</span>
							<div className="kpi-big">
								<span className="kpi-big-num" style={{ color: PALETTE.crypto }}>{ totalDigital }</span>
								<span className="kpi-big-text">en cartera</span>
							</div>
							{ totalDigital > 0 && (
								<div className="kpi-mini-counters">
									<span className="mini-counter"><span className="mc-label">Acc.</span> <span className="mc-num">{ acciones }</span></span>
									<span className="mini-counter"><span className="mc-label">Crip.</span> <span className="mc-num">{ criptos }</span></span>
									<span className="mini-counter"><span className="mc-label">Bon.</span> <span className="mc-num">{ bonos }</span></span>
								</div>
							) }
						</div>

						<div className="kpi-block">
							<span className="micro-label">Mayor exposición</span>
							<div className="kpi-exposure">
								{ hasAnyExposure ? (
									<>
										<span className="exposure-type">{ TYPE_LABELS[topType] }</span>
										<span className="exposure-pct" style={{ color: exposureColor }}>
											{ (pcts[topType] * 100).toFixed(1).replace('.', ',') }%
										</span>
									</>
								) : (
									<span className="exposure-type">—</span>
								) }
							</div>
						</div>

						<div className="kpi-block">
							<span className="micro-label">Top 3 posiciones</span>
							{ (!hasAnyExposure || top3.length === 0) ? (
								<div className="top3-empty">Sin posiciones</div>
							) : (
								<ul className="top3-list">
									{ top3.map((item, idx) => {
										const badge = badgeFor(item);
										const pct = (item.total_amount / grand) * 100;
										return (
											<li key={ idx } className="top3-row">
												<span className="top3-index">{ idx + 1 }.</span>
												<span className={ `top3-badge top3-badge-${ badge.cls }` }>{ badge.text }</span>
												<span className="top3-name">{ this.truncate(item.name, 16) }</span>
												<span className="top3-amount">{ Globals.formatMiles(item.total_amount) }</span>
												<span className="top3-pct">{ pct.toFixed(1).replace('.', ',') }%</span>
											</li>
										);
									}) }
								</ul>
							) }
						</div>
					</div>
				</div>
			</div>
		);
	};

	renderSubtypeBigCard = (
		args: {
			key: string;
			cardClass: string;
			label: string;
			dotColor: string;
			positions: Position[];
			canvasRef: React.RefObject<HTMLCanvasElement>;
		}
	) => {
		const { cardClass, label, dotColor, positions, canvasRef } = args;
		const grand = this.grandTotal();
		const subtotal = positions.reduce((acc, p) => acc + p.total_amount, 0);
		const sorted = [...positions].sort((a, b) => b.total_amount - a.total_amount);
		const colors = getCategoricalColors(sorted.length);
		return (
			<div className={ `prot-card ${ cardClass }` }>
				<div className="prot-card-deco">
					<div className="prot-card-line"></div>
				</div>
				<div className="prot-card-content">
					<div className="prot-card-header">
						<div className="prot-card-title">
							<span className="prot-dot" style={{ backgroundColor: dotColor, boxShadow: `0 0 8px ${ dotColor }` }}></span>
							<span className="prot-label">{ label }</span>
						</div>
						<div className="prot-card-amounts">
							<div className="prot-total">{ Globals.formatMiles(subtotal) }</div>
							<div className="prot-pct-grand">{ this.percent(subtotal, grand) } del patrimonio</div>
						</div>
					</div>
					{ positions.length === 0 ? (
						<div className="prot-empty">Sin posiciones en { label.toLowerCase() }</div>
					) : (
						<div className="prot-card-body">
							<div className="prot-doughnut-wrapper">
								<canvas ref={ canvasRef }></canvas>
								<div className="prot-doughnut-center">
									<div className="prot-doughnut-num">{ positions.length }</div>
									<div className="prot-doughnut-text">posiciones</div>
								</div>
							</div>
							<ul className="prot-legend">
								{ sorted.map((p, i) => (
									<li key={ i }>
										<span className="dot" style={{ backgroundColor: colors[i] }}></span>
										<span className="name">{ this.truncate(p.name, 20) }</span>
										<span
											className="pct pct-badge"
											style={{ color: colors[i], backgroundColor: hexToRgba(colors[i], 0.22) }}
										>{ this.percent(p.total_amount, subtotal) }</span>
										<span className="amount">{ Globals.formatMiles(p.total_amount) }</span>
									</li>
								)) }
							</ul>
						</div>
					) }
				</div>
			</div>
		);
	};

	renderProtagonists = () => {
		const cryptoPositions = this.getSubtypePositions('Criptos');
		const stocksPositions = this.getSubtypePositions('Acciones');
		return (
			<div className="section-wrap">
				<div className="section-tagline">— Activos digitales — Detalle por categoría</div>
				<div className="protagonists-grid">
					{ this.renderSubtypeBigCard({
						key: 'crypto',
						cardClass: 'prot-card-crypto',
						label: 'Criptos',
						dotColor: PALETTE.crypto,
						positions: cryptoPositions,
						canvasRef: this.cryptoDonutRef
					}) }
					{ this.renderSubtypeBigCard({
						key: 'stocks',
						cardClass: 'prot-card-stocks',
						label: 'Acciones',
						dotColor: PALETTE.stocks,
						positions: stocksPositions,
						canvasRef: this.stocksDonutRef
					}) }
				</div>
			</div>
		);
	};

	renderSecondaryCard = (
		args: {
			label: string;
			dotColor: string;
			positions: Position[];
			canvasRef: React.RefObject<HTMLCanvasElement>;
			topN?: number;
		}
	) => {
		const { label, dotColor, positions, canvasRef, topN } = args;
		const grand = this.grandTotal();
		const subtotal = positions.reduce((acc, p) => acc + p.total_amount, 0);
		const sorted = [...positions].sort((a, b) => b.total_amount - a.total_amount);
		const limit = topN || sorted.length;
		const visible = sorted.slice(0, limit);
		const extra = Math.max(0, sorted.length - limit);
		const colors = getCategoricalColors(sorted.length);
		return (
			<div className="sec-card">
				<div className="sec-card-header">
					<div className="sec-card-title">
						<span className="sec-dot" style={{ backgroundColor: dotColor }}></span>
						<span className="sec-label">{ label }</span>
					</div>
					<div className="sec-card-amounts">
						<div className="sec-total">{ Globals.formatMiles(subtotal) }</div>
						<div className="sec-pct">{ this.percent(subtotal, grand) }</div>
					</div>
				</div>
				{ positions.length === 0 ? (
					<div className="sec-empty">Sin posiciones</div>
				) : (
					<div className="sec-card-body">
						<div className="sec-doughnut-wrapper">
							<canvas ref={ canvasRef }></canvas>
						</div>
						<ul className="sec-legend">
							{ visible.map((p, i) => (
								<li key={ i }>
									<span className="dot" style={{ backgroundColor: colors[i] }}></span>
									<span className="name">{ this.truncate(p.name, 18) }</span>
									<span
										className="pct pct-badge"
										style={{ color: colors[i], backgroundColor: hexToRgba(colors[i], 0.22) }}
									>{ this.percent(p.total_amount, subtotal) }</span>
									<span className="amount">{ Globals.formatMiles(p.total_amount) }</span>
								</li>
							)) }
							{ extra > 0 && (
								<li className="sec-extra">+ { extra } { extra === 1 ? 'posición' : 'posiciones' } más</li>
							) }
						</ul>
					</div>
				) }
			</div>
		);
	};

	renderSecondary = () => {
		const digitalPositions = this.state.positions[TYPE.DIGITAL] || [];
		const liquidityPositions = this.state.positions[TYPE.LIQUIDITY] || [];
		return (
			<div className="section-wrap">
				<div className="section-tagline">— Vista consolidada</div>
				<div className="secondary-grid">
					{ this.renderSecondaryCard({
						label: 'Activos digitales (total)',
						dotColor: PALETTE.digital,
						positions: digitalPositions,
						canvasRef: this.digitalDonutRef,
						topN: 5
					}) }
					{ this.renderSecondaryCard({
						label: 'Liquidez',
						dotColor: PALETTE.liquidity,
						positions: liquidityPositions,
						canvasRef: this.liquidityDonutRef
					}) }
				</div>
			</div>
		);
	};
	render() {
		const { loading } = this.state;

		return (
			<div id="admin-money-client-dashboard">
				{ this.renderError() }

				{ loading ? (
					<div className="loading-wrapper">
						<Loading />
					</div>
				) : (
					<>
						{ this.renderHeader() }
						{ this.renderProtagonists() }
						{ this.renderSecondary() }
					</>
				) }

				<div id="chart-tooltip" className="chart-tooltip-external" />
			</div>
		);
	}
}

export default connect((state: RootState) => {
	return {
		user: state.user
	};
})(ClientDashboard);
